import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Button from './Button';

const Table = ({
  data = [],
  columns = [],
  loading = false,
  sortable = true,
  pagination = true,
  pageSize = 20,
  initialPage = 1,
  // Server-side pagination support
  serverPaginated = false,
  totalCount = null,
  currentPage: controlledCurrentPage,
  onPageChange,
  onPageSizeChange,
  className = '',
  emptyMessage = 'No data available',
  onRowClick,
  selectedRows = [],
  onRowSelect,
  selectable = false,
  keyField = 'id', // Field to use as unique key for rows
}) => {
  const [uncontrolledPage, setUncontrolledPage] = useState(initialPage || 1);
  const currentPage = controlledCurrentPage || uncontrolledPage;
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Sorting logic
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  // Pagination logic (client vs server)
  const effectiveTotal = serverPaginated && typeof totalCount === 'number' ? totalCount : sortedData.length;
  const totalPages = Math.max(1, Math.ceil(effectiveTotal / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = pagination
    ? (serverPaginated ? sortedData : sortedData.slice(startIndex, endIndex))
    : sortedData;

  const handleSort = key => {
    if (!sortable) return;

    setSortConfig(prevConfig => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === 'asc'
          ? 'desc'
          : 'asc',
    }));
  };

  const handlePageChange = page => {
    if (!controlledCurrentPage) setUncontrolledPage(page);
    if (onPageChange) onPageChange(page);
  };

  const handleSelectAll = checked => {
    if (!onRowSelect) return;

    if (checked) {
      const allIds = currentData.map(row => row[keyField]);
      onRowSelect([...new Set([...selectedRows, ...allIds])]);
    } else {
      const currentIds = currentData.map(row => row[keyField]);
      onRowSelect(selectedRows.filter(id => !currentIds.includes(id)));
    }
  };

  const handleRowSelect = (rowId, checked) => {
    if (!onRowSelect) return;

    if (checked) {
      onRowSelect([...selectedRows, rowId]);
    } else {
      onRowSelect(selectedRows.filter(id => id !== rowId));
    }
  };

  const isAllSelected =
    currentData.length > 0 &&
    currentData.every(row => selectedRows.includes(row[keyField]));
  const isIndeterminate =
    currentData.some(row => selectedRows.includes(row[keyField])) && !isAllSelected;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-yellow"></div>
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }

  return (
    <div className={`table-container ${className}`}>
      <div className="table-scrollable">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-[#fff7e0]">
            <tr>
              {selectable && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={input => {
                      if (input) input.indeterminate = isIndeterminate;
                    }}
                    onChange={e => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-brand-yellow focus:ring-brand-yellow border-gray-300 rounded"
                  />
                </th>
              )}
              {columns.map(column => (
                <th
                  key={column.key}
                  className={`px-4 py-2 text-left text-xs font-medium text-[#601e00] uppercase tracking-wider ${
                    sortable && column.sortable !== false
                      ? 'cursor-pointer hover:bg-gray-100'
                      : ''
                  }`}
                  onClick={() =>
                    sortable &&
                    column.sortable !== false &&
                    handleSort(column.key)
                  }
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.title}</span>
                    {sortable && column.sortable !== false && (
                      <div className="flex flex-col">
                        <svg
                          className={`w-3 h-3 ${
                            sortConfig.key === column.key &&
                            sortConfig.direction === 'asc'
                              ? 'text-[#601e00]'
                              : 'text-gray-400'
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" />
                        </svg>
                        <svg
                          className={`w-3 h-3 -mt-1 ${
                            sortConfig.key === column.key &&
                            sortConfig.direction === 'desc'
                              ? 'text-[#601e00]'
                              : 'text-gray-400'
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                        </svg>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="px-6 py-4 text-center text-gray-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              currentData.map((row, index) => (
                <tr
                  key={row[keyField] || index}
                  className={`table-row ${onRowClick ? 'cursor-pointer' : ''} ${
                    selectedRows.includes(row[keyField]) ? 'bg-brand-yellow/10' : ''
                  }`}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {selectable && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(row[keyField])}
                        onChange={e => {
                          e.stopPropagation();
                          handleRowSelect(row[keyField], e.target.checked);
                        }}
                        className="h-4 w-4 text-brand-yellow focus:ring-brand-yellow border-gray-300 rounded"
                      />
                    </td>
                  )}
                  {columns.map(column => (
                    <td
                      key={column.key}
                      className="px-4 py-2 whitespace-nowrap text-sm text-gray-900"
                    >
                      {column.render
                        ? column.render(row[column.key], row)
                        : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{serverPaginated ? startIndex + 1 : startIndex + 1}</span> to{' '}
                <span className="font-medium">{serverPaginated ? Math.min(endIndex, effectiveTotal) : Math.min(endIndex, sortedData.length)}</span>{' '}
                of <span className="font-medium">{serverPaginated ? effectiveTotal : sortedData.length}</span> results
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Rows per page</span>
                <select
                  className="appearance-none border rounded-md text-sm px-3 py-1.5 bg-white text-gray-900 border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#fdbd0a] focus:border-[#fdbd0a]"
                  value={pageSize}
                  onChange={(e) => onPageSizeChange && onPageSizeChange(Number(e.target.value))}
                >
                  {[10, 20, 50, 100].map(sz => (
                    <option key={sz} value={sz} className="text-gray-900">{sz}</option>
                  ))}
                </select>
              </div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm overflow-hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-[#601e00] hover:bg-[#fff7e0] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`relative inline-flex items-center px-3 py-2 border text-sm font-medium transition-colors ${
                      page === currentPage
                        ? 'bg-[#fdbd0a] border-[#fdbd0a] text-[#601e00]'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-[#fff7e0]'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-3 py-2 border border-gray-300 bg-white text-sm font-medium text-[#601e00] hover:bg-[#fff7e0] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

Table.propTypes = {
  data: PropTypes.array,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      render: PropTypes.func,
    })
  ),
  loading: PropTypes.bool,
  sortable: PropTypes.bool,
  pagination: PropTypes.bool,
  pageSize: PropTypes.number,
  className: PropTypes.string,
  emptyMessage: PropTypes.string,
  onRowClick: PropTypes.func,
  selectedRows: PropTypes.array,
  onRowSelect: PropTypes.func,
  selectable: PropTypes.bool,
  keyField: PropTypes.string,
};

export default Table;
